/**
 * Owns the home page's showcase rows, entirely client-side.
 *
 * The first four rows used to arrive from `getStaticProps` behind an hour of
 * ISR. That cannot coexist with per-buyer ordering -- a cached page serves one
 * session's ordering to everyone -- so the whole lifecycle moved here.
 *
 * The category-group order is derived from `Cats` on the client rather than
 * passed down as a prop, because it is static generated data the browser
 * already has. One fewer thing for the server to compute and hand over.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { Cats } from '../generated/static-categories';
import { trackError, ErrorSeverity } from '../utils/error-tracking';
import type { HomeShowcase } from '../components/search-listing/types';
import { isPageEnteredByBack } from '../utils/navigation-direction';

/**
 * Category groups the home page never shows: All (0), Pets (16),
 * Miscellaneous (27) and Jobs (31). Previously duplicated in
 * `pages/index.tsx`'s loader; it lives here now that the client owns the list.
 */
const EXCLUDED_GROUPS = new Set([0, 16, 27, 31]);

/** Rows handed to the page per click. */
export const HOME_SHOWCASES_PER_PAGE = 4;

/**
 * Category groups asked for in one round trip.
 *
 * Deliberately more than a page of rows. The server drops any group holding
 * fewer than MIN_PRODUCTS_PER_GROUP listings (see pages/api/search/
 * home-showcases.ts), so a slice of four groups can legitimately come back with
 * nothing in it. Asking wide and buffering the surplus is what keeps a click
 * from landing on a run of thin groups and rendering nothing.
 */
const GROUPS_PER_REQUEST = 6;

/**
 * How many round trips one fill may make before giving up.
 *
 * Bounded rather than unbounded: a tail of groups that are all thin would
 * otherwise walk the entire list on a single click. Whatever is not reached
 * stays unconsumed for the next one.
 */
const MAX_REQUESTS_PER_FILL = 4;

/** Minimum listings for a group to be worth a row. */
const MIN_PRODUCTS_PER_GROUP = 7;

/**
 * Every category group the home page may show, in the static default order.
 *
 * Only the fallback now: the page's first request asks the server to rank these
 * (utils/home-row-rank.ts) and walks the order it sends back. Without that
 * answer (an old server, a failed rank) the page walks this.
 */
export function defaultCategoryGroupOrder(): number[] {
  return Object.entries(Cats)
    .filter(([id]) => !EXCLUDED_GROUPS.has(Number(id)))
    .sort(([, a], [, b]) => a.sortOrder - b.sortOrder)
    .map(([id]) => Number(id));
}

export interface UseHomeShowcasesOptions {
  enabled: boolean;
  /**
   * Category groups in the order they should be requested. Defaults to the
   * static order; the caller passes a session-ranked order when it has one.
   */
  groupOrder?: number[];
  /**
   * The session's tag-affinity vector, sent with every page so the server
   * orders each row by overlap. Frozen at mount with the group order, for the
   * same reason: rows already painted must not be re-ranked under the buyer.
   */
  affinityTags?: Array<{ tag: string; weight: number }>;
  /**
   * Have the server rank the groups on the first request: it gets every group,
   * serves the top GROUPS_PER_REQUEST and returns the whole order and the
   * scores (utils/home-row-rank.ts).
   */
  rank?: boolean;
  /** This session's interest per group, sent with the ranking request. */
  interest?: Array<{ categoryGroupId: number; weight: number }>;
  /**
   * A picked sort (utils/listing-sort.ts): each row's listings come in that
   * order; the row order stays ranked. A change starts the rows over.
   */
  sortBy?: string;
  fetchHomeShowcases: (params: {
    minProductsPerGroup: number;
    categoryGroups: number[];
    affinityTags?: Array<{ tag: string; weight: number }>;
    pinnedGroups?: number[];
    rankGroups?: boolean;
    serveCount?: number;
    interest?: Array<{ categoryGroupId: number; weight: number }>;
    sortBy?: string;
  }) => Promise<{ showcases: HomeShowcase[]; deferredGroups?: number[]; groupOrder?: number[]; groupScores?: Record<string, number> }>;
}

export interface UseHomeShowcasesResult {
  rows: HomeShowcase[];
  /** True only while the FIRST page is in flight, so the shell can hold its shape. */
  isLoadingInitial: boolean;
  isLoadingMore: boolean;
  /** True while official category rows remain -- buffered or unfetched. */
  hasMore: boolean;
  /** Appends the next page. Resolves with how many rows it actually added. */
  loadMore: () => Promise<number>;
  /** How many groups have been requested so far, for the tag-showcase handover. */
  fetchedGroupCount: number;
  totalGroupCount: number;
  /** Each group's score from the server's ranking; empty when it did not rank. */
  groupScores: Record<string, number>;
  /** How many of `rows` arrived with the first page. */
  firstPageCount: number;
}

/**
 * The rows home last showed, and where its walk stood, for Back.
 *
 * Back to home refetched every row and held the skeleton for seconds; on a
 * slow answer it looked like no list at all (2026-10-26, the Showcase view the
 * home-default-view test gives half of new visitors). The grid has kept its
 * list since 2026-10-20 (utils/browse-list-cache.ts); this is the same for the
 * rows. In memory: Back within the tab. A reload, a forward visit or another
 * sort fetches as before.
 */
interface KeptRows {
  sortBy?: string;
  order: number[];
  rows: HomeShowcase[];
  buffer: HomeShowcase[];
  cursor: number;
  walkOrder: number[];
  pinned: number[];
  groupScores: Record<string, number>;
  firstPageCount: number;
}
let kept: KeptRows | null = null;

/** Test seam. */
export function clearHomeShowcasesMemory(): void {
  kept = null;
}

export function useHomeShowcases({
  enabled,
  groupOrder,
  affinityTags,
  rank = false,
  interest,
  sortBy,
  fetchHomeShowcases,
}: UseHomeShowcasesOptions): UseHomeShowcasesResult {
  // Back to home: the rows it showed, at once, with nothing fetched.
  const [restored] = useState(() => (isPageEnteredByBack() && kept && kept.sortBy === sortBy ? kept : null));
  const [rows, setRows] = useState<HomeShowcase[]>(() => restored?.rows ?? []);
  const [groupScores, setGroupScores] = useState<Record<string, number>>(() => restored?.groupScores ?? {});
  const [firstPageCount, setFirstPageCount] = useState(() => restored?.firstPageCount ?? 0);
  /** Whether the ranking request is still owed: the first request only. */
  const rankPendingRef = useRef(restored ? false : rank);
  const [interestFrozen] = useState(() => (interest?.length ? interest.map(i => ({ ...i })) : undefined));
  /**
   * Whether the first page has finished, one way or the other.
   *
   * `isLoadingInitial` is derived from this rather than stored. As a state set
   * inside the first-page effect it read `false` for the render in which
   * `enabled` turned on, before that effect ran -- a frame in which the page
   * had neither rows nor a loading flag, so it showed whatever else it had
   * instead of the skeleton.
   */
  const [initialSettled, setInitialSettled] = useState(!!restored);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [fetchedGroupCount, setFetchedGroupCount] = useState(() => restored?.cursor ?? 0);

  /**
   * The group order, FROZEN at mount.
   *
   * Re-ranking the remaining groups mid-scroll would reorder rows the buyer has
   * already passed, which is the same reflow the append-only rule exists to
   * avoid. State rather than a ref so it can be read during render without
   * fighting the rules of hooks.
   *
   * A caller that wants a session-ranked order must therefore have it before it
   * flips `enabled` -- which is the right shape anyway, since the session
   * interest vector is read synchronously from storage.
   */
  const [order] = useState<number[]>(() =>
    restored ? restored.order : groupOrder?.length ? [...groupOrder] : defaultCategoryGroupOrder()
  );
  const [affinity] = useState(() => (affinityTags?.length ? affinityTags.map(t => ({ ...t })) : undefined));

  /**
   * The order as it is actually walked: the frozen order, plus any group the
   * server deferred, appended at the end.
   *
   * A group the reader said "Show less" to (on any device) comes back from the
   * server in `deferredGroups` instead of as a row. It goes to the back of the
   * line here, and is asked for again there with `pinnedGroups`, which tells the
   * server it has already been pushed back once. Appending never moves a row
   * already painted, so the append-only rule above still holds.
   */
  const orderRef = useRef<number[]>(restored?.walkOrder ?? order);
  const pinnedRef = useRef<Set<number>>(new Set(restored?.pinned ?? []));
  const [totalGroupCount, setTotalGroupCount] = useState(() => (restored?.walkOrder ?? order).length);

  /**
   * Rows fetched but not yet handed to the page.
   *
   * This is the whole point of the rewrite. The server drops any category group
   * with fewer than MIN_PRODUCTS_PER_GROUP listings, so a slice of groups can
   * come back empty through no fault of the click that asked for it. The old
   * shape fetched exactly one slice per click and appended
   * `if (next.length > 0)` -- but advanced the cursor either way, so a run of
   * thin groups spent clicks and rendered nothing. The reported symptom was five
   * rows, two dead clicks, then rows again.
   *
   * SearchListing.tsx already carries the same fix for the category page, where
   * the comment records the same cost: "Three clicks was the usual cost of
   * walking past the thin ones one batch at a time." This is that fix for home,
   * plus the part that one lacks -- the next page is fetched before it is asked
   * for, so a click serves from memory instead of a round trip.
   *
   * Refs, not state: every read happens inside an async run where a state value
   * captured at click time would already be stale.
   */
  const bufferRef = useRef<HomeShowcase[]>(restored?.buffer ?? []);
  /** Groups consumed so far. The async counterpart of `fetchedGroupCount`. */
  const cursorRef = useRef(restored?.cursor ?? 0);
  /**
   * The cursor as of the last request that LANDED, which is what Back keeps.
   * `cursorRef` moves when a request starts: kept mid-flight, it skipped the
   * groups whose rows had not arrived yet, and Back never showed them.
   */
  const landedCursorRef = useRef(restored?.cursor ?? 0);
  /** Mirrors the buffer's length for render; `hasMore` has to see it. */
  const [bufferedCount, setBufferedCount] = useState(() => restored?.buffer.length ?? 0);

  const startedRef = useRef(!!restored);

  /**
   * Bumped when the sort changes: the rows start over in the new order. A
   * fetch begun for the old sort that lands after the change is dropped.
   */
  const [generation, setGeneration] = useState(0);
  const generationRef = useRef(0);
  const sortRef = useRef(sortBy);

  /**
   * Serializes work without dropping it.
   *
   * The previous guard was a boolean ref that made an overlapping call return
   * immediately. That was correct for two rapid scroll triggers and wrong for a
   * click landing during the background prefetch: the click was silently
   * discarded, which is the same dead button by another route. Queueing behind
   * the in-flight run instead means a click always produces rows -- it just
   * waits for the fetch already paying for them.
   */
  const pendingRef = useRef<Promise<unknown> | null>(null);
  const serialize = useCallback(<T,>(job: () => Promise<T>): Promise<T> => {
    const next = (pendingRef.current ?? Promise.resolve()).then(job, job);
    pendingRef.current = next.catch(() => undefined);
    return next;
  }, []);

  /**
   * Pull group-slices until the buffer holds `want` rows or the groups run out.
   *
   * The cursor advances per REQUEST, not per row: a thin group is consumed by
   * having been asked about, and asking again would return the same nothing.
   */
  const fill = useCallback(
    async (want: number): Promise<void> => {
      for (let request = 0; request < MAX_REQUESTS_PER_FILL; request++) {
        if (bufferRef.current.length >= want) return;

        const start = cursorRef.current;
        // The ranking request sends every group; the server serves the top ones.
        const ranking = rankPendingRef.current && start === 0;
        const slice = ranking ? orderRef.current : orderRef.current.slice(start, start + GROUPS_PER_REQUEST);
        if (slice.length === 0) return;
        rankPendingRef.current = false;

        cursorRef.current = start + Math.min(slice.length, GROUPS_PER_REQUEST);
        setFetchedGroupCount(cursorRef.current);

        const pinned = slice.filter(id => pinnedRef.current.has(id));
        const startedIn = generationRef.current;
        const { showcases, deferredGroups, groupOrder: rankedOrder, groupScores: scores } = await fetchHomeShowcases({
          minProductsPerGroup: MIN_PRODUCTS_PER_GROUP,
          categoryGroups: slice,
          ...(sortRef.current ? { sortBy: sortRef.current } : {}),
          ...(affinity ? { affinityTags: affinity } : {}),
          ...(pinned.length > 0 ? { pinnedGroups: pinned } : {}),
          ...(ranking ? { rankGroups: true, serveCount: GROUPS_PER_REQUEST, ...(interestFrozen ? { interest: interestFrozen } : {}) } : {}),
        });

        // The sort changed while this was in flight: these rows are the old order's.
        if (startedIn !== generationRef.current) return;

        // Walk the server's order from here on. Nothing is painted yet, so
        // adopting it moves no row. A group the server left out keeps its
        // place at the end.
        if (ranking && rankedOrder && rankedOrder.length > 0) {
          const known = new Set(rankedOrder);
          orderRef.current = [...rankedOrder, ...orderRef.current.filter(id => !known.has(id))];
          setTotalGroupCount(orderRef.current.length);
          // Only the top of the server's order was served; anything after it,
          // including groups it left out, is still to walk.
          cursorRef.current = Math.min(GROUPS_PER_REQUEST, rankedOrder.length);
          setFetchedGroupCount(cursorRef.current);
          if (scores) setGroupScores(scores);
        } else if (ranking) {
          // A server that did not rank served every group it was sent.
          cursorRef.current = slice.length;
          setFetchedGroupCount(cursorRef.current);
        }

        if (showcases.length > 0) {
          bufferRef.current = [...bufferRef.current, ...showcases];
          setBufferedCount(bufferRef.current.length);
        }

        // Once only: a pinned group is never deferred again, so this cannot loop.
        const toBack = (deferredGroups ?? []).filter(id => !pinnedRef.current.has(id));
        if (toBack.length > 0) {
          for (const id of toBack) pinnedRef.current.add(id);
          orderRef.current = [...orderRef.current, ...toBack];
          setTotalGroupCount(orderRef.current.length);
        }

        landedCursorRef.current = cursorRef.current;
      }
    },
    [fetchHomeShowcases, affinity, interestFrozen]
  );

  /** Take up to `count` rows off the front of the buffer. */
  const take = useCallback((count: number): HomeShowcase[] => {
    const taken = bufferRef.current.slice(0, count);
    bufferRef.current = bufferRef.current.slice(taken.length);
    setBufferedCount(bufferRef.current.length);
    return taken;
  }, []);

  /**
   * Put the next page on deck.
   *
   * Failure is deliberately quiet: nothing is rendered from a prefetch, so the
   * only consequence of losing one is that the next click fetches for itself,
   * which is what it used to do every time.
   */
  const prefetch = useCallback(() => {
    void serialize(() => fill(HOME_SHOWCASES_PER_PAGE)).catch(() => undefined);
  }, [serialize, fill]);

  /**
   * First page. One shot.
   *
   * `fetchPage`'s identity is now stable -- `order` is frozen state and
   * `fetchHomeShowcases` is memoized by its caller -- which matters more than it
   * looks. When it churned, this effect re-ran, its cleanup set `cancelled` on
   * the ALREADY-IN-FLIGHT first fetch, and the re-run returned early on
   * `startedRef`, so nothing ever cleared `isLoadingInitial`: the home page held
   * its loading skeleton permanently while the rows sat fetched and unrendered.
   */
  // A new sort starts the rows over: same groups, re-ranked, each row in the new order.
  useEffect(() => {
    if (sortRef.current === sortBy) return;
    sortRef.current = sortBy;
    generationRef.current += 1;
    bufferRef.current = [];
    cursorRef.current = 0;
    landedCursorRef.current = 0;
    orderRef.current = [...order];
    pinnedRef.current = new Set();
    rankPendingRef.current = rank;
    startedRef.current = false;
    /* eslint-disable react-hooks/set-state-in-effect -- the sort lives in the URL; this resets the paged rows to follow it */
    setBufferedCount(0);
    setFetchedGroupCount(0);
    setTotalGroupCount(order.length);
    setGroupScores({});
    setFirstPageCount(0);
    setRows([]);
    setInitialSettled(false);
    setGeneration(g => g + 1);
    /* eslint-enable react-hooks/set-state-in-effect */
  }, [sortBy, order, rank]);

  useEffect(() => {
    if (!enabled || startedRef.current) return;
    startedRef.current = true;

    let cancelled = false;

    void (async () => {
      try {
        // Queued like every other run, so a load-more triggered while page one
        // is still in flight waits for it rather than re-requesting the same
        // groups and appending the first rows twice.
        await serialize(async () => {
          await fill(HOME_SHOWCASES_PER_PAGE);
          if (!cancelled) {
            const firstRows = take(HOME_SHOWCASES_PER_PAGE);
            setFirstPageCount(firstRows.length);
            setRows(firstRows);
          }
        });
      } catch (error) {
        // A failed first page leaves `rows` empty and `isLoadingInitial` false,
        // which the caller renders as the grid -- a real fallback rather than a
        // permanently empty showcase shell. Still reported: silently showing a
        // grid where the whole home layout should be is exactly the kind of
        // degradation that goes unnoticed for months.
        trackError('Error fetching more showcases', ErrorSeverity.WARNING, {
          component: 'SearchListing',
          extra: { error: String(error), stage: 'initial' },
        });
      } finally {
        if (!cancelled) setInitialSettled(true);
      }

      // The first click should not have to wait for a network round trip.
      if (!cancelled) prefetch();
    })();

    return () => {
      cancelled = true;
    };
  }, [enabled, serialize, fill, take, prefetch, generation]);

  // Keep what is shown, and where the walk stands, for Back.
  useEffect(() => {
    if (!enabled || !initialSettled || rows.length === 0) return;
    kept = {
      sortBy: sortRef.current,
      order,
      rows,
      buffer: bufferRef.current,
      cursor: landedCursorRef.current,
      walkOrder: orderRef.current,
      pinned: [...pinnedRef.current],
      groupScores,
      firstPageCount,
    };
  }, [enabled, initialSettled, rows, bufferedCount, fetchedGroupCount, totalGroupCount, groupScores, firstPageCount, order]);

  const loadMore = useCallback(async (): Promise<number> => {
    if (!enabled) return 0;

    let appended = 0;
    setIsLoadingMore(true);
    try {
      await serialize(async () => {
        // Only fetch if the prefetch has not already paid for this click.
        if (bufferRef.current.length === 0) await fill(HOME_SHOWCASES_PER_PAGE);

        const next = take(HOME_SHOWCASES_PER_PAGE);
        if (next.length === 0) return;

        appended = next.length;
        // APPEND-ONLY. Rows already painted keep their position: re-sorting the
        // accumulated array would splice a newly-arrived row above one the buyer
        // has already scrolled past, which reads as the page jumping under them.
        setRows(prev => [...prev, ...next]);
      });
    } catch (error) {
      // Leave what is already rendered; the trigger can fire again on scroll.
      trackError('Error fetching more showcases', ErrorSeverity.WARNING, {
        component: 'SearchListing',
        extra: { error: String(error), stage: 'more' },
      });
    } finally {
      setIsLoadingMore(false);
    }

    if (appended > 0) prefetch();
    return appended;
  }, [enabled, serialize, fill, take, prefetch]);

  return {
    rows,
    isLoadingInitial: enabled && !initialSettled,
    isLoadingMore,
    // Buffered rows count: the cursor runs ahead of what is rendered now that
    // pages are fetched before they are asked for, so the old
    // `fetchedGroupCount < totalGroupCount` would call it done with rows still
    // held back.
    hasMore: bufferedCount > 0 || fetchedGroupCount < totalGroupCount,
    loadMore,
    fetchedGroupCount,
    totalGroupCount,
    groupScores,
    firstPageCount,
  };
}
